import React, { useEffect, useState } from "react";
import axios from "../axios";
import "./AttendanceSheet.css";

const API_BASE = process.env.REACT_APP_API_URL + "api/";

const AttendanceSheet = () => {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("today"); // 'today', 'history', 'monthly'
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7) // YYYY-MM
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Daily attendance state for bulk operations
  const [dailyAttendance, setDailyAttendance] = useState({});
  
  // Time editing state
  const [editingTime, setEditingTime] = useState(null);
  const [tempTime, setTempTime] = useState({ checkIn: "", checkOut: "" });

  // Monthly data
  const [monthlyData, setMonthlyData] = useState([]);

  const token = localStorage.getItem("access_token");

  const api = axios.create({
    baseURL: API_BASE,
    headers: { Authorization: token ? `Bearer ${token}` : "" },
  });

  // Date helper functions
  const isToday = (date) => {
    return date === new Date().toISOString().split("T")[0];
  };

  const isPastDate = (date) => {
    return date < new Date().toISOString().split("T")[0];
  };

  const isFutureDate = (date) => {
    return date > new Date().toISOString().split("T")[0];
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Date navigation functions
  const goToPreviousDay = () => {
    const date = new Date(selectedDate + "T00:00:00");
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const goToNextDay = () => {
    const date = new Date(selectedDate + "T00:00:00");
    date.setDate(date.getDate() + 1);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  // Fetch Employees
  const fetchEmployees = async () => {
    try {
      const res = await api.get("employees/");
      const data = res.data.results || res.data;
      setEmployees(data.filter(emp => emp.employee_status === "Active"));
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  // Fetch Attendance
  const fetchAttendance = async (date = selectedDate) => {
    setLoading(true);
    try {
      const res = await api.get("attendance/", {
        params: { date }
      });
      const data = res.data.results || res.data;
      setAttendance(data);
      
      // Initialize daily attendance state
      const dailyMap = {};
      data.forEach(att => {
        dailyMap[att.employee] = {
          id: att.id,
          status: att.status,
          check_in: att.check_in || "",
          check_out: att.check_out || "",
          remarks: att.remarks || "",
        };
      });
      setDailyAttendance(dailyMap);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Monthly Attendance
  const fetchMonthlyAttendance = async (month = selectedMonth) => {
    setLoading(true);
    try {
      const res = await api.get("attendance/", {
        params: { 
          date__gte: `${month}-01`,
          date__lte: `${month}-31`
        }
      });
      const data = res.data.results || res.data;
      setMonthlyData(data);
    } catch (err) {
      console.error("Error fetching monthly attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeView === "today" || activeView === "history") {
      fetchAttendance(selectedDate);
    } else if (activeView === "monthly") {
      fetchMonthlyAttendance(selectedMonth);
    }
  }, [selectedDate, selectedMonth, activeView]);

  // Mark attendance for a single employee
  const markAttendance = async (employeeId, status, checkIn = "", checkOut = "") => {
    const existingRecord = dailyAttendance[employeeId];
    
    const payload = {
      employee: employeeId,
      date: selectedDate,
      status,
      check_in: checkIn || "",
      check_out: checkOut || "",
      remarks: existingRecord?.remarks || "",
    };

    const cleanedPayload = {};
    Object.keys(payload).forEach(key => {
      if (payload[key] !== "" && payload[key] !== null && payload[key] !== undefined) {
        cleanedPayload[key] = payload[key];
      }
    });

    try {
      if (existingRecord?.id) {
        await api.patch(`attendance/${existingRecord.id}/`, cleanedPayload);
      } else {
        const res = await api.post("attendance/", cleanedPayload);
        setDailyAttendance(prev => ({
          ...prev,
          [employeeId]: { ...cleanedPayload, id: res.data.id }
        }));
      }
      fetchAttendance(selectedDate);
    } catch (err) {
      console.error("Error marking attendance:", err);
      alert("Error: " + (err.response?.data ? JSON.stringify(err.response.data) : err.message));
    }
  };

  // Quick mark present
  const markPresent = (employeeId) => {
    let checkInTime;
    
    if (isToday(selectedDate)) {
      // For today, use current time
      const now = new Date();
      checkInTime = now.toTimeString().slice(0, 5);
    } else {
      // For past/future dates, use default 9:00 AM
      checkInTime = "09:00";
    }
    
    markAttendance(employeeId, "Present", checkInTime, "");
  };

  // Mark all present
  const markAllPresent = () => {
    const dateText = isToday(selectedDate) ? "today" : selectedDate;
    if (!window.confirm(`Mark all employees as present for ${dateText}?`)) return;
    
    let checkInTime;
    if (isToday(selectedDate)) {
      const now = new Date();
      checkInTime = now.toTimeString().slice(0, 5);
    } else {
      checkInTime = "09:00";
    }
    
    employees.forEach(emp => {
      markAttendance(emp.employee_id, "Present", checkInTime, "");
    });
  };

  // Update check-out time
  const updateCheckOut = (employeeId) => {
    const record = dailyAttendance[employeeId];
    if (!record || record.status !== "Present") {
      alert("Employee must be marked present first");
      return;
    }
    
    let checkOutTime;
    if (isToday(selectedDate)) {
      const now = new Date();
      checkOutTime = now.toTimeString().slice(0, 5);
    } else {
      checkOutTime = "18:00";
    }
    
    markAttendance(employeeId, "Present", record.check_in, checkOutTime);
  };

  // Start editing time
  const startEditTime = (employeeId) => {
    const record = dailyAttendance[employeeId];
    if (!record) {
      alert("Please mark attendance first");
      return;
    }
    
    setEditingTime(employeeId);
    setTempTime({
      checkIn: record.check_in || "",
      checkOut: record.check_out || "",
    });
  };

  // Cancel time editing
  const cancelEditTime = () => {
    setEditingTime(null);
    setTempTime({ checkIn: "", checkOut: "" });
  };

  // Save edited time
  const saveEditedTime = async (employeeId) => {
    const record = dailyAttendance[employeeId];
    if (!record) return;

    // Validate times
    if (tempTime.checkIn && tempTime.checkOut) {
      const [inH, inM] = tempTime.checkIn.split(":").map(Number);
      const [outH, outM] = tempTime.checkOut.split(":").map(Number);
      const inMinutes = inH * 60 + inM;
      const outMinutes = outH * 60 + outM;
      
      if (outMinutes <= inMinutes) {
        alert("Check-out time must be after check-in time");
        return;
      }
    }

    await markAttendance(
      employeeId, 
      record.status, 
      tempTime.checkIn || record.check_in, 
      tempTime.checkOut || record.check_out
    );
    
    setEditingTime(null);
    setTempTime({ checkIn: "", checkOut: "" });
  };

  // Delete attendance record
  const deleteAttendance = async (employeeId) => {
    const record = dailyAttendance[employeeId];
    if (!record?.id) return;

    if (!window.confirm("Remove attendance record?")) return;

    try {
      await api.delete(`attendance/${record.id}/`);
      const newDaily = { ...dailyAttendance };
      delete newDaily[employeeId];
      setDailyAttendance(newDaily);
      fetchAttendance(selectedDate);
    } catch (err) {
      alert("Error deleting attendance");
    }
  };

  // Get employee by ID
  const getEmployeeById = (empId) => {
    return employees.find(e => e.employee_id === empId);
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         emp.employee_id.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (statusFilter === "all") return matchesSearch;
    
    const record = dailyAttendance[emp.employee_id];
    if (statusFilter === "unmarked") return matchesSearch && !record;
    return matchesSearch && record?.status === statusFilter;
  });

  // Calculate statistics
  const stats = {
    total: employees.length,
    present: Object.values(dailyAttendance).filter(r => r.status === "Present").length,
    absent: Object.values(dailyAttendance).filter(r => r.status === "Absent").length,
    leave: Object.values(dailyAttendance).filter(r => r.status === "Leave").length,
    halfDay: Object.values(dailyAttendance).filter(r => r.status === "Half Day").length,
    unmarked: employees.length - Object.keys(dailyAttendance).length,
  };

  // Check if late
  const isLate = (checkIn) => {
    if (!checkIn) return false;
    const [hours, minutes] = checkIn.split(":").map(Number);
    return hours > 9 || (hours === 9 && minutes > 30);
  };

  // Calculate monthly stats for each employee
  const getMonthlyStats = (employeeId) => {
    const empRecords = monthlyData.filter(r => r.employee === employeeId);
    const present = empRecords.filter(r => r.status === "Present").length;
    const absent = empRecords.filter(r => r.status === "Absent").length;
    const leave = empRecords.filter(r => r.status === "Leave").length;
    const halfDay = empRecords.filter(r => r.status === "Half Day").length;
    const total = empRecords.length;
    const percentage = total > 0 ? ((present + halfDay * 0.5) / total * 100).toFixed(1) : 0;
    
    return { present, absent, leave, halfDay, total, percentage };
  };

  // Get days in month
  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split("-").map(Number);
    return new Date(year, month, 0).getDate();
  };

  // Render TODAY view
  const renderTodayView = () => (
    <div className="attendance-today">
      {/* Date Status Banner */}
      {!isToday(selectedDate) && (
        <div className={`date-status-banner ${isPastDate(selectedDate) ? 'past-date' : 'future-date'}`}>
          <div className="banner-icon">
            {isPastDate(selectedDate) ? '📜' : '🔮'}
          </div>
          <div className="banner-content">
            <div className="banner-title">
              {isPastDate(selectedDate) ? 'Viewing Past Date' : 'Viewing Future Date'}
            </div>
            <div className="banner-date">{formatDisplayDate(selectedDate)}</div>
          </div>
          <button className="btn-goto-today" onClick={goToToday}>
            📅 Go to Today
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-total">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Employees</div>
          </div>
        </div>

        <div className="stat-card stat-present">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.present}</div>
            <div className="stat-label">Present</div>
          </div>
        </div>

        <div className="stat-card stat-absent">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-value">{stats.absent}</div>
            <div className="stat-label">Absent</div>
          </div>
        </div>

        <div className="stat-card stat-leave">
          <div className="stat-icon">🏖️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.leave}</div>
            <div className="stat-label">On Leave</div>
          </div>
        </div>

        <div className="stat-card stat-halfday">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <div className="stat-value">{stats.halfDay}</div>
            <div className="stat-label">Half Day</div>
          </div>
        </div>

        <div className="stat-card stat-unmarked">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.unmarked}</div>
            <div className="stat-label">Unmarked</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <div className="date-navigation-section">
          <button 
            className="btn-date-nav btn-prev" 
            onClick={goToPreviousDay}
            title="Previous Day"
          >
            ← Previous Day
          </button>
          
          <div className="date-picker-wrapper">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-picker"
              max="2099-12-31"
            />
            {!isToday(selectedDate) && (
              <button 
                className="btn-today-quick" 
                onClick={goToToday}
                title="Jump to Today"
              >
                Today
              </button>
            )}
          </div>
          
          <button 
            className="btn-date-nav btn-next" 
            onClick={goToNextDay}
            title="Next Day"
          >
            Next Day →
          </button>
        </div>

        <button className="btn-quick-action btn-mark-all" onClick={markAllPresent}>
          ✅ Mark All Present
        </button>
      </div>

      {/* Filters */}
      <div className="attendance-filters">
        <input
          type="text"
          placeholder="🔍 Search by name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="unmarked">Unmarked</option>
          <option value="Present">Present</option>
          <option value="Absent">Absent</option>
          <option value="Leave">On Leave</option>
          <option value="Half Day">Half Day</option>
        </select>
      </div>

      {/* Employee Cards */}
      <div className="employee-cards">
        {loading ? (
          <div className="loading-state">Loading employees...</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="empty-state">No employees found</div>
        ) : (
          filteredEmployees.map(emp => {
            const record = dailyAttendance[emp.employee_id];
            const status = record?.status || "unmarked";
            const checkIn = record?.check_in || "";
            const checkOut = record?.check_out || "";
            const late = checkIn && isLate(checkIn);
            const isEditing = editingTime === emp.employee_id;

            return (
              <div key={emp.employee_id} className={`employee-card status-${status.toLowerCase().replace(" ", "-")}`}>
                <div className="employee-card-header">
                  <div className="employee-info">
                    {emp.photo ? (
                      <img src={emp.photo} alt={emp.name} className="employee-avatar" />
                    ) : (
                      <div className="employee-avatar-placeholder">
                        {emp.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="employee-details">
                      <div className="employee-name">{emp.name}</div>
                      <div className="employee-id">{emp.employee_id}</div>
                    </div>
                  </div>
                  
                  {status !== "unmarked" && (
                    <div className={`status-badge status-${status.toLowerCase().replace(" ", "-")}`}>
                      {status}
                    </div>
                  )}
                </div>

                {status === "Present" && (
                  <div className="time-info">
                    {isEditing ? (
                      <div className="time-edit-mode">
                        <div className="time-edit-entry">
                          <label>Check In:</label>
                          <input
                            type="time"
                            value={tempTime.checkIn}
                            onChange={(e) => setTempTime({...tempTime, checkIn: e.target.value})}
                            className="time-input"
                          />
                        </div>
                        <div className="time-edit-entry">
                          <label>Check Out:</label>
                          <input
                            type="time"
                            value={tempTime.checkOut}
                            onChange={(e) => setTempTime({...tempTime, checkOut: e.target.value})}
                            className="time-input"
                          />
                        </div>
                        <div className="time-edit-actions">
                          <button 
                            className="btn-time-save"
                            onClick={() => saveEditedTime(emp.employee_id)}
                          >
                            ✓ Save
                          </button>
                          <button 
                            className="btn-time-cancel"
                            onClick={cancelEditTime}
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="time-entry">
                          <span className="time-label">Check In:</span>
                          <span className={`time-value ${late ? "late" : ""}`}>
                            {checkIn || "-"}
                            {late && <span className="late-badge">Late</span>}
                          </span>
                        </div>
                        <div className="time-entry">
                          <span className="time-label">Check Out:</span>
                          <span className="time-value">{checkOut || "-"}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="employee-card-actions">
                  {status === "unmarked" ? (
                    <>
                      <button
                        className="btn-action btn-present"
                        onClick={() => markPresent(emp.employee_id)}
                      >
                        ✅ Present
                      </button>
                      <button
                        className="btn-action btn-absent"
                        onClick={() => markAttendance(emp.employee_id, "Absent")}
                      >
                        ❌ Absent
                      </button>
                      <button
                        className="btn-action btn-leave"
                        onClick={() => markAttendance(emp.employee_id, "Leave")}
                      >
                        🏖️ Leave
                      </button>
                      <button
                        className="btn-action btn-halfday"
                        onClick={() => markAttendance(emp.employee_id, "Half Day")}
                      >
                        ⏰ Half Day
                      </button>
                    </>
                  ) : status === "Present" && !isEditing ? (
                    <>
                      {!checkOut && (
                        <button
                          className="btn-action btn-checkout"
                          onClick={() => updateCheckOut(emp.employee_id)}
                        >
                          🚪 Check Out
                        </button>
                      )}
                      <button
                        className="btn-action btn-edit-time"
                        onClick={() => startEditTime(emp.employee_id)}
                      >
                        ✏️ Edit Time
                      </button>
                      <button
                        className="btn-action btn-delete"
                        onClick={() => deleteAttendance(emp.employee_id)}
                      >
                        🗑️ Remove
                      </button>
                    </>
                  ) : !isEditing ? (
                    <button
                      className="btn-action btn-delete"
                      onClick={() => deleteAttendance(emp.employee_id)}
                    >
                      🗑️ Remove
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  // Render HISTORY view
  const renderHistoryView = () => (
    <div className="attendance-history">
      <div className="history-controls">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="date-picker"
        />
        <input
          type="text"
          placeholder="🔍 Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Date</th>
              <th>Status</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Duration</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">Loading...</td>
              </tr>
            ) : attendance.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">No attendance records</td>
              </tr>
            ) : (
              attendance
                .filter(att => {
                  const emp = getEmployeeById(att.employee);
                  if (!emp) return false;
                  return emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         emp.employee_id.toLowerCase().includes(searchQuery.toLowerCase());
                })
                .map(att => {
                  const emp = getEmployeeById(att.employee);
                  if (!emp) return null;

                  let duration = "-";
                  if (att.check_in && att.check_out) {
                    const [inH, inM] = att.check_in.split(":").map(Number);
                    const [outH, outM] = att.check_out.split(":").map(Number);
                    const totalMinutes = (outH * 60 + outM) - (inH * 60 + inM);
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    duration = `${hours}h ${minutes}m`;
                  }

                  return (
                    <tr key={att.id}>
                      <td>
                        <div className="employee-cell">
                          <strong>{emp.name}</strong>
                          <br />
                          <small>{emp.employee_id}</small>
                        </div>
                      </td>
                      <td>{att.date}</td>
                      <td>
                        <span className={`badge badge-${att.status.toLowerCase().replace(" ", "-")}`}>
                          {att.status}
                        </span>
                      </td>
                      <td className={isLate(att.check_in) ? "late-text" : ""}>
                        {att.check_in || "-"}
                        {isLate(att.check_in) && <span className="late-indicator"> (Late)</span>}
                      </td>
                      <td>{att.check_out || "-"}</td>
                      <td>{duration}</td>
                      <td>{att.remarks || "-"}</td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Render MONTHLY view
  const renderMonthlyView = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    const [year, month] = selectedMonth.split("-").map(Number);

    return (
      <div className="attendance-monthly">
        <div className="monthly-controls">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="month-picker"
          />
          <input
            type="text"
            placeholder="🔍 Search employee..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="monthly-summary-cards">
          {loading ? (
            <div className="loading-state">Loading monthly data...</div>
          ) : (
            employees
              .filter(emp => 
                emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.employee_id.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map(emp => {
                const stats = getMonthlyStats(emp.employee_id);
                
                return (
                  <div key={emp.employee_id} className="monthly-summary-card">
                    <div className="monthly-card-header">
                      <div className="employee-info">
                        {emp.photo ? (
                          <img src={emp.photo} alt={emp.name} className="employee-avatar-small" />
                        ) : (
                          <div className="employee-avatar-placeholder-small">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="employee-details">
                          <div className="employee-name">{emp.name}</div>
                          <div className="employee-id">{emp.employee_id}</div>
                        </div>
                      </div>
                      <div className="attendance-percentage">
                        <div className="percentage-circle" style={{
                          background: `conic-gradient(
                            var(--status-present) ${stats.percentage * 3.6}deg,
                            var(--bg-light) 0deg
                          )`
                        }}>
                          <div className="percentage-inner">
                            {stats.percentage}%
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="monthly-stats-grid">
                      <div className="monthly-stat">
                        <div className="monthly-stat-icon">✅</div>
                        <div className="monthly-stat-value">{stats.present}</div>
                        <div className="monthly-stat-label">Present</div>
                      </div>
                      <div className="monthly-stat">
                        <div className="monthly-stat-icon">❌</div>
                        <div className="monthly-stat-value">{stats.absent}</div>
                        <div className="monthly-stat-label">Absent</div>
                      </div>
                      <div className="monthly-stat">
                        <div className="monthly-stat-icon">🏖️</div>
                        <div className="monthly-stat-value">{stats.leave}</div>
                        <div className="monthly-stat-label">Leave</div>
                      </div>
                      <div className="monthly-stat">
                        <div className="monthly-stat-icon">⏰</div>
                        <div className="monthly-stat-value">{stats.halfDay}</div>
                        <div className="monthly-stat-label">Half Day</div>
                      </div>
                    </div>

                    <div className="monthly-total">
                      Total Days Recorded: <strong>{stats.total}</strong> / {daysInMonth}
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="attendance-sheet">
      <div className="attendance-header">
        <h1>📅 Attendance Management</h1>
        <div className="view-tabs">
          <button
            className={`view-tab ${activeView === "today" ? "active" : ""}`}
            onClick={() => setActiveView("today")}
          >
            📋 Today
          </button>
          <button
            className={`view-tab ${activeView === "history" ? "active" : ""}`}
            onClick={() => setActiveView("history")}
          >
            📊 History
          </button>
          <button
            className={`view-tab ${activeView === "monthly" ? "active" : ""}`}
            onClick={() => setActiveView("monthly")}
          >
            📆 Monthly Summary
          </button>
        </div>
      </div>

      <div className="attendance-content">
        {activeView === "today" && renderTodayView()}
        {activeView === "history" && renderHistoryView()}
        {activeView === "monthly" && renderMonthlyView()}
      </div>
    </div>
  );
};

export default AttendanceSheet;